import { useState } from "react";
import {
  ExclamationCircleOutlined,
  InfoCircleOutlined
} from "@ant-design/icons";
import { Button, Tooltip } from "antd";
import { DateUtils, DATE_FORMAT_MMDDYYYY } from "utils/dateutils";
import MaterialDetailsMobileDrawer from "./material-details-mobile-drawer";
import { MaterialDateConfirmationData, MaterialDetailsData } from "./types";

function formatDisplayDate(date: string) {
  if (!date) return "-";
  return DateUtils.format(date, DATE_FORMAT_MMDDYYYY);
}

function getExpectedOnsiteDelayTooltip(currentDelay: number | null) {
  if (!currentDelay || currentDelay <= 0) return "";
  const dayLabel = currentDelay === 1 ? "day" : "days";
  return `Current delivery is ${currentDelay} working ${dayLabel} after the required onsite date.`;
}

function MaterialDateConfirmationHeader({
  data,
  detailsData,
  expectedOnsiteDateLabel = "Projected Onsite Date"
}: {
  data: Pick<
    MaterialDateConfirmationData,
    | "materialTitle"
    | "requiredOnsiteDate"
    | "currentExpectedOnsiteDate"
    | "isExpectedOnsiteAtRisk"
    | "currentDelay"
  >;
  detailsData: MaterialDetailsData;
  expectedOnsiteDateLabel?: string;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <>
      <div
        className="border-0 border-b border-solid border-color-2 px-4 lg:px-7 py-4 flex flex-wrap items-start justify-between gap-4"
        data-testid="material-date-confirmation-header"
      >
        <div className="flex items-start gap-2 min-w-0">
          <div className="text-base lg:text-lg font-semibold text-color-4">
            {data.materialTitle}
          </div>
          <div className="lg:hidden flex items-center shrink-0">
            <Button
              type="text"
              size="small"
              icon={<InfoCircleOutlined className="text-color-9" />}
              className="text-color-9"
              onClick={() => {
                setDetailsOpen(true);
              }}
              data-testid="material-details-info-button"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-start gap-6 lg:gap-10">
          <div>
            <div className="text-xs font-semibold text-color-1 uppercase tracking-wide">
              Required Onsite Date
            </div>
            <div className="text-sm text-color-3 mt-1">
              {formatDisplayDate(data.requiredOnsiteDate)}
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold text-color-1 uppercase tracking-wide">
              {expectedOnsiteDateLabel}
            </div>
            <div
              className={`text-sm mt-1 flex items-center gap-1.5 ${
                data.isExpectedOnsiteAtRisk
                  ? "text-ciq-error-500"
                  : "text-color-3"
              }`}
            >
              {formatDisplayDate(data.currentExpectedOnsiteDate)}
              {data.isExpectedOnsiteAtRisk && (
                <Tooltip
                  title={getExpectedOnsiteDelayTooltip(data.currentDelay)}
                >
                  <ExclamationCircleOutlined className="text-ciq-error-500 hover:text-ciq-error-500 text-sm" />
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
      <MaterialDetailsMobileDrawer
        open={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
        }}
        data={detailsData}
      />
    </>
  );
}

export default MaterialDateConfirmationHeader;
